<script lang="ts">
import type {
  TransferDirection,
  TransferItem,
} from 'ant-design-vue/es/transfer'

import type { ExtractPropTypes, PropType } from 'vue'

import { computed, defineComponent, nextTick, onMounted, ref, watch } from 'vue'

import { DragOutlined } from '@ant-design/icons-vue'
import { Checkbox, theme, Tooltip, Transfer } from 'ant-design-vue'
import draggable from 'vuedraggable'

// 定义props类型
const transferProps = {
  targetKeys: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  dataSource: {
    type: Array as PropType<TransferItem[]>,
    default: () => [],
  },
  sourceKeys: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  titles: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  operations: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  filterOption: {
    type: Function as PropType<
      (inputValue: string, item: TransferItem) => boolean
    >,
    default: () => {
      return false
    },
  },
  searchPlaceholder: {
    type: String,
    default: '搜索',
  },
  notFoundContent: {
    type: String,
    default: '列表为空',
  },
  listStyle: {
    type: [Object, Function] as PropType<any>,
    default: () => {
      return {}
    },
  },
  showSelectAll: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  oneWay: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: [Object, Boolean] as PropType<any>,
    default: () => {
      return {}
    },
  },
  status: {
    type: String,
  },
  selectionsIcon: {
    type: String,
  },
  renderItem: {
    type: Function as PropType<any>,
  },
  footer: {
    type: Function as PropType<any>,
  },
  locale: {
    type: Object,
  },
  validateTransfer: {
    type: Function as PropType<
      (ctx: {
        moveKeys: string[]
        direction: TransferDirection
        sourceKeys: string[]
        targetKeys: string[]
      }) => boolean
    >,
    default: () => {
      return () => true
    },
  },
}

// 提取props类型
type TransferProps = ExtractPropTypes<typeof transferProps>

export default defineComponent({
  name: 'DraggableTransfer',
  components: {
    Transfer,
    Draggable: draggable,
    DragOutlined,
    Checkbox,
    Tooltip,
  },
  props: transferProps,
  emits: [
    'update:targetKeys',
    'change',
    'scroll',
    'search',
    'selectChange',
    'update:sourceKeys',
  ],
  setup(props: TransferProps, { emit, attrs }: { emit: any; attrs: any }) {
    const { token } = theme.useToken()

    // 用于存储源列表的自定义顺序
    const innerSourceKeys = ref<string[]>([])

    // 存储每个项目是否超出的状态
    const textOverflow = ref<Record<string, boolean>>({})

    // 同步外部和内部sourceKeys
    watch(
      () => props.sourceKeys,
      (newSourceKeys) => {
        if (newSourceKeys && newSourceKeys.length > 0) {
          innerSourceKeys.value = [...newSourceKeys]
        }
      },
    )

    // 初始化和更新 sourceKeys
    watch(
      () => props.dataSource,
      (newDataSource) => {
        const allKeys = newDataSource.map(
          (item: TransferItem) => item.key as string,
        )
        const newSourceKeys = allKeys.filter(
          (key: string) => !props.targetKeys.includes(key),
        )

        // 如果提供了外部sourceKeys，则使用外部的顺序
        if (props.sourceKeys && props.sourceKeys.length > 0) {
          // 使用外部提供的sourceKeys，但过滤掉不在新数据源中的key
          const validSourceKeys = props.sourceKeys.filter((key: string) =>
            newSourceKeys.includes(key),
          )
          // 添加新数据源中有但外部sourceKeys没有的key
          const keysToAdd = newSourceKeys.filter(
            (key: string) => !props.sourceKeys.includes(key),
          )
          innerSourceKeys.value = [...validSourceKeys, ...keysToAdd]
        } else {
          // 没有提供外部sourceKeys，保持原有逻辑
          // 保留之前存在的顺序，仅添加新的key到末尾
          const existingKeys = innerSourceKeys.value.filter((key: string) =>
            newSourceKeys.includes(key),
          )
          const newKeys = newSourceKeys.filter(
            (key: string) => !existingKeys.includes(key),
          )
          innerSourceKeys.value = [...existingKeys, ...newKeys]
        }
      },
      { immediate: true },
    )

    // 当 targetKeys 变化时，更新 sourceKeys
    watch(
      () => props.targetKeys,
      (newTargetKeys) => {
        // 从 sourceKeys 中移除转移到 target 的 key
        innerSourceKeys.value = innerSourceKeys.value.filter(
          (key: string) => !newTargetKeys.includes(key),
        )

        // 添加从 target 转移回来的 key
        const allKeys = props.dataSource.map(
          (item: TransferItem) => item.key as string,
        )
        const shouldBeInSource = allKeys.filter(
          (key: string) => !newTargetKeys.includes(key),
        )
        const keysToAdd = shouldBeInSource.filter(
          (key: string) => !innerSourceKeys.value.includes(key),
        )
        innerSourceKeys.value = [...innerSourceKeys.value, ...keysToAdd]

        // 同步到外部
        emit('update:sourceKeys', innerSourceKeys.value)
      },
    )

    // 源数据列表 - 按照 sourceKeys 的顺序
    const sourceDataList = computed(() => {
      return innerSourceKeys.value
        .map((key: string) =>
          props.dataSource.find((item: TransferItem) => item.key === key),
        )
        .filter(Boolean) as TransferItem[]
    })

    // 目标数据列表 - 按照 targetKeys 的顺序
    const targetDataList = computed(() => {
      return props.targetKeys
        .map((key: string) =>
          props.dataSource.find((item: TransferItem) => item.key === key),
        )
        .filter(Boolean) as TransferItem[]
    })

    // 拖拽时触发的事件处理
    const onDragEnd = (data: TransferItem[], direction: TransferDirection) => {
      if (direction === 'left') {
        // 源列表拖拽，更新 sourceKeys
        const newSourceKeys = data.map((item) => item.key as string)
        innerSourceKeys.value = newSourceKeys
        // 同步到外部
        emit('update:sourceKeys', innerSourceKeys.value)
      } else {
        // 目标列表拖拽，更新 targetKeys
        const newTargetKeys = data.map((item) => item.key as string)
        emit('update:targetKeys', newTargetKeys)
        // 不触发change事件，避免Transfer组件的默认行为干扰我们的自定义顺序
      }

      // 在拖拽后更新文本溢出状态
      nextTick(() => {
        checkTextOverflow()
      })
    }

    // 检查文本是否超出容器
    const checkTextOverflow = () => {
      const textElements = document.querySelectorAll(
        '.item-title-inner',
      ) as NodeListOf<HTMLElement>
      textElements.forEach((el) => {
        const key = el.dataset.key
        if (key) {
          // 比较实际宽度和可见宽度，判断是否溢出
          textOverflow.value[key] = el.scrollWidth > el.clientWidth
        }
      })
    }

    // 当列表数据变化时，检查文本溢出
    watch(
      () => [...sourceDataList.value, ...targetDataList.value],
      () => {
        nextTick(() => {
          checkTextOverflow()
        })
      },
      { deep: true },
    )

    // 组件挂载后检查文本溢出
    onMounted(() => {
      nextTick(() => {
        checkTextOverflow()
      })
    })

    // 处理Transfer自身的事件
    const handleChange = (
      nextTargetKeys: string[],
      direction: TransferDirection,
      moveKeys: string[],
    ) => {
      // 调用校验函数，如果返回false则不进行转移
      const isValid = props.validateTransfer({
        moveKeys,
        direction,
        sourceKeys: innerSourceKeys.value,
        targetKeys: props.targetKeys,
      })

      if (!isValid) {
        // 校验失败，还原到原来的状态
        emit('update:targetKeys', props.targetKeys)
        return
      }

      emit('update:targetKeys', nextTargetKeys)
      emit('change', nextTargetKeys, direction, moveKeys)

      // 在列表项变化后重新检查文本溢出
      nextTick(() => {
        checkTextOverflow()
      })
    }

    const handleScroll = (direction: TransferDirection, e: Event) => {
      emit('scroll', direction, e)
    }

    const handleSearch = (direction: TransferDirection, value: string) => {
      emit('search', direction, value)
    }

    const handleSelectChange = (
      sourceSelectedKeys: string[],
      targetSelectedKeys: string[],
    ) => {
      emit('selectChange', sourceSelectedKeys, targetSelectedKeys)
    }

    // 提取所有额外的props传递给Transfer组件
    const getTransferProps = computed(() => {
      const {
        targetKeys: _targetKeys,
        dataSource: _dataSource,
        sourceKeys: _sourceKeys,
        ...rest
      } = props

      return {
        ...rest,
        ...attrs,
      }
    })

    // 使用token定义样式
    const transferStyles = computed(() => ({
      dragHandle: {
        color: token.value.colorTextSecondary,
      },
      dragHandleHover: {
        color: token.value.colorPrimary,
      },
      ghost: {
        background: token.value.colorPrimaryBg,
        borderColor: token.value.colorPrimary,
      },
      chosen: {
        backgroundColor: token.value.colorPrimaryBgHover,
        boxShadow: `0 0 10px rgba(0, 0, 0, 0.2)`,
      },
      item: {
        borderBottomColor: token.value.colorSplit,
        backgroundColor: token.value.colorBgContainer,
        height: '35px',
      },
      itemHover: {
        backgroundColor: token.value.colorBgElevated,
      },
      sortableChosen: {
        backgroundColor: token.value.colorPrimaryBgHover,
        borderColor: token.value.colorPrimary,
        boxShadow: `0 0 8px ${token.value.colorPrimaryActive}20`,
      },
      sortableGhost: {
        background: token.value.colorPrimaryBg,
        borderColor: token.value.colorPrimary,
      },
      transferList: {
        borderColor: token.value.colorBorder,
      },
    }))

    return {
      sourceDataList,
      targetDataList,
      onDragEnd,
      handleChange,
      handleScroll,
      handleSearch,
      handleSelectChange,
      textOverflow,
      checkTextOverflow,
      getTransferProps,
      transferStyles,
    }
  },
})
</script>

<template>
  <Transfer
    v-bind="getTransferProps"
    :data-source="dataSource"
    :target-keys="targetKeys"
    @change="handleChange"
    @scroll="handleScroll"
    @search="handleSearch"
    @select-change="handleSelectChange"
    :list-style="{
      height: '300px',
      flex: '1',
      minWidth: '120px',
    }"
    class="draggable-transfer"
  >
    <template #children="{ direction, selectedKeys, onItemSelect }">
      <div class="ant-transfer-list-body-customize-wrapper">
        <Draggable
          :list="direction === 'left' ? sourceDataList : targetDataList"
          :item-key="(item: any) => item.key"
          ghost-class="ghost"
          chosen-class="chosen"
          handle=".drag-handle"
          @end="
            onDragEnd(
              direction === 'left' ? sourceDataList : targetDataList,
              direction,
            )
          "
          :scroll="true"
          :scroll-sensitivity="80"
          :scroll-speed="20"
          animation="300"
          :group="{
            name: `transfer-list-${direction}`,
            pull: false,
            put: false,
          }"
        >
          <template #item="{ element }">
            <div
              class="ant-transfer-list-content-item"
              :class="[
                {
                  'ant-transfer-list-content-item-disabled': element.disabled,
                  'ant-transfer-list-content-item-checked':
                    selectedKeys.includes(element.key),
                },
              ]"
              :style="transferStyles.item"
            >
              <!-- 使用 ant-design-vue 的 Checkbox 组件 -->
              <Checkbox
                :disabled="element.disabled"
                :checked="selectedKeys.includes(element.key)"
                @click.stop
                @change="
                  onItemSelect(element.key, !selectedKeys.includes(element.key))
                "
              />

              <span class="ant-transfer-list-content-item-text">
                <span
                  class="drag-handle"
                  v-if="!element.disabled"
                  :style="transferStyles.dragHandle"
                >
                  <DragOutlined />
                </span>
                <!-- 根据实际溢出情况决定是否使用Tooltip -->
                <span class="item-title">
                  <template v-if="textOverflow[element.key]">
                    <Tooltip :title="element.title" placement="topLeft">
                      <span class="item-title-inner" :data-key="element.key">{{
                        element.title
                      }}</span>
                    </Tooltip>
                  </template>
                  <template v-else>
                    <span class="item-title-inner" :data-key="element.key">{{
                      element.title
                    }}</span>
                  </template>
                </span>
              </span>
            </div>
          </template>
          <template
            #header
            v-if="
              (direction === 'left' ? sourceDataList : targetDataList)
                .length === 0
            "
          >
            <div class="ant-transfer-list-body-not-found">
              <AEmpty />
            </div>
          </template>
        </Draggable>
      </div>
    </template>
  </Transfer>
</template>

<style scoped>
.draggable-transfer {
  display: flex;
  width: 100%;
  overflow: visible;
}

.drag-handle {
  margin-right: 8px;
  cursor: move;
  display: inline-flex;
  align-items: center;
}

.drag-handle:hover {
  color: v-bind('transferStyles.dragHandleHover.color');
}

/* 文本溢出时显示省略号 */
.item-title {
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  max-width: calc(100% - 25px); /* 减去拖拽图标的宽度 */
  height: 22px; /* 固定高度，与checkbox保持一致 */
}

.item-title-inner {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  width: 100%;
  line-height: 22px; /* 确保行高与高度一致 */
}

/* 拖拽时的占位元素样式 */
.ghost {
  opacity: 0.5;
  background: v-bind('transferStyles.ghost.background');
  border: 1px dashed v-bind('transferStyles.ghost.borderColor') !important;
  margin-bottom: 1px; /* 确保底部边框可见 */
  position: relative; /* 确保定位上下文 */
  z-index: 2; /* 提高层级，避免被遮挡 */
  box-sizing: border-box; /* 确保边框计算在内 */
}

.chosen {
  background-color: v-bind('transferStyles.chosen.backgroundColor');
  box-shadow: v-bind('transferStyles.chosen.boxShadow');
}

/* 滚动容器样式 */
.ant-transfer-list-body-customize-wrapper {
  height: 100%;
  overflow: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 重要：防止flex容器溢出 */
}

/* 确保拖拽项有正确的边框和间距 */
:deep(.ant-transfer-list-content-item) {
  display: flex;
  align-items: center; /* 改回垂直居中 */
  padding: 6px 12px;
  border-bottom: 1px solid v-bind('transferStyles.item.borderBottomColor');
  transition: all 0.3s;
  cursor: default; /* 默认鼠标样式，在拖拽手柄上会覆盖为move */
  position: relative; /* 帮助定位 */
  z-index: 1; /* 确保正常的z-index */
  box-sizing: border-box; /* 确保边框计算在内 */
  margin-bottom: 1px; /* 让底部边框更清晰 */
  min-height: 32px; /* 保持最小高度 */
}

/* 统一hover效果，确保选中和未选中状态下的hover效果一致 */
:deep(.ant-transfer-list-content-item:hover) {
  background-color: v-bind(
    'transferStyles.itemHover.backgroundColor'
  ) !important;
  cursor: pointer;
}

/* 确保选中项的hover效果与未选中项一致 */
:deep(.ant-transfer-list-content-item-checked:hover) {
  background-color: v-bind(
    'transferStyles.itemHover.backgroundColor'
  ) !important;
}

/* 复选框垂直居中 */
:deep(.ant-checkbox-wrapper) {
  margin-right: 8px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 22px;
}

/* 文本区域样式 */
:deep(.ant-transfer-list-content-item-text) {
  flex: 1;
  overflow: hidden; /* 确保内容溢出时不会撑开容器 */
  white-space: nowrap; /* 强制单行显示 */
  text-overflow: ellipsis; /* 超出显示省略号 */
  display: flex;
  align-items: center;
  height: 22px; /* 固定高度，使内容垂直居中 */
}

/* 拖拽时的样式 */
:deep(.sortable-chosen) {
  background-color: v-bind('transferStyles.sortableChosen.backgroundColor');
  border: 1px dashed v-bind('transferStyles.sortableChosen.borderColor') !important;
  opacity: 1;
  z-index: 2;
  box-shadow: v-bind('transferStyles.sortableChosen.boxShadow');
}

:deep(.sortable-ghost) {
  opacity: 0.5;
  background: v-bind('transferStyles.sortableGhost.background');
  border: 1px dashed v-bind('transferStyles.sortableGhost.borderColor') !important;
  margin-bottom: 1px; /* 确保底部边框可见 */
  z-index: 2;
  box-sizing: border-box;
}

/* 确保Transfer组件原始滚动样式被保留 */
:deep(.ant-transfer-list) {
  border: 1px solid v-bind('transferStyles.transferList.borderColor');
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  min-width: 120px; /* 设置最小宽度，确保宽度不会因为为空而缩小 */
  flex: 1; /* 使用flex: 1来自适应宽度 */
}

:deep(.ant-transfer-list-body) {
  flex: 1;
  overflow: auto;
  position: relative;
  min-height: 150px; /* 恢复原来的最小高度 */
}

/* 修复容器高度 */
:deep(.ant-transfer-list-content-wrapper) {
  overflow: hidden;
}

/* 设置滚动区域样式 */
:deep(.ant-transfer-list-content) {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* 解决边框被遮挡的问题 */
:deep(.ant-transfer-list-content) > * {
  margin-bottom: 1px;
}

:deep(.ant-transfer-list-content-item:last-child) {
  margin-bottom: 1px;
  border-bottom: 1px solid v-bind('transferStyles.item.borderBottomColor');
}

/* 确保整个Transfer组件宽度稳定 */
:deep(.ant-transfer) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%; /* 设置宽度为100%，确保填满容器 */
}

/* 确保当一侧为空时也显示可拖拽区域 */
:deep(.ant-transfer-list-body-not-found) {
  flex: 1;
  min-height: 150px; /* 恢复原来的最小高度 */
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
